/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import TwoFactorConfirmModal from "./TwoFactorConfirmModal";
import TwoFactorSetupModal from "./TwoFactorSetupModal";

import { useRouter } from "routes/hooks/useRouter";
import { Auth } from "aws-amplify";

const initialData: {
  current_password: string;
  new_password: string;
  confirm_new_password: string;
} = {
  current_password: "",
  new_password: "",
  confirm_new_password: "",
};

const SecurityTab: React.FC<{
  setSwalProps: any;
  isFetching: boolean;
  userProfile: any;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  isProcessing: boolean;
}> = ({ setSwalProps, userProfile, setIsProcessing, isProcessing }) => {
  const [resetPasswordData, setResetPasswordData] = useState<any>(initialData);
  const [currentPasswordIncorrect, setCurrentPasswordIncorrect] =
    useState<boolean>(false);
  const [isOpenTwoFactorConfirmModal, setIsOpenTwoFactorConfirmModal] =
    useState<boolean>(false);
  const [isOpenTwoFactorSetupModal, setIsOpenTwoFactorSetupModal] =
    useState<boolean>(false);

  const userRole = localStorage.getItem("userRole");
  const username = localStorage.getItem("username");
  const userEmail = localStorage.getItem("userEmail");

  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean>(
    userProfile?.isUserMFAEnabled || false
  );
  const [isForceResetTwoFactor, setIsForceResetTwoFactor] = useState<boolean>(
    userProfile?.isForceResetTwoFactor || false
  );

  const router = useRouter();

  // Sync state with userProfile when it loads
  useEffect(() => {
    if (userProfile?.isUserMFAEnabled !== undefined) {
      setTwoFactorEnabled(userProfile.isUserMFAEnabled);
    }
    if (userProfile?.isForceResetTwoFactor !== undefined) {
      setIsForceResetTwoFactor(userProfile.isForceResetTwoFactor);
    }
  }, [userProfile?.isUserMFAEnabled, userProfile?.isForceResetTwoFactor]);

  // Determine if we should show Enable or Disable button
  // Show "Enable" if: isForceResetTwoFactor=true OR twoFactorEnabled=false
  // Show "Disable" if: twoFactorEnabled=true AND isForceResetTwoFactor=false
  const shouldShowEnableButton = isForceResetTwoFactor || !twoFactorEnabled;

  const handleInputChange = async (event: any) => {
    setCurrentPasswordIncorrect(false);
    const { id, value } = event.target;
    setResetPasswordData({ ...resetPasswordData, [id]: value });
  };

  const handleSubmit = async () => {
    try {
      setIsProcessing(true);
      const userInfo = await Auth.currentAuthenticatedUser();
      await Auth.signIn(
        userInfo.attributes.email,
        resetPasswordData.current_password
      );

      try {
        const data = {
          new_password: resetPasswordData.new_password,
          userRole: userRole,
          username: username,
        };

        await axios
          .post(
            process.env.REACT_APP_API_URL + `/api/${userRole.toLocaleLowerCase()}/user/action/change-password`,
            data
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Your password has been changed, please sign-in again for security reason",
                showConfirmButton: true,
                didClose: async () => {
                  await Auth.signOut();
                  localStorage.clear();
                  router.push("/auth/sign-in");
                },
              });
            } else {
              setResetPasswordData(initialData);
              setIsProcessing(false);
            }
          })
          .catch(function (error) {
            setIsProcessing(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setResetPasswordData(initialData);
                setSwalProps({});
                setIsProcessing(false);
              },
            });
          });
      } catch (error: any) {
        setResetPasswordData(initialData);
        setIsProcessing(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsProcessing(false);
          },
        });
      }
    } catch (error: any) {
      setResetPasswordData(initialData);
      setIsProcessing(false);
      setCurrentPasswordIncorrect(true);
    }
  };

  const handleTwoFactorToggle = () => {
    showTwoFactorConfirmModal();
  };

  const showTwoFactorConfirmModal = () => {
    setIsOpenTwoFactorConfirmModal(true);
  };

  const closeTwoFactorConfirmModal = () => {
    setIsOpenTwoFactorConfirmModal(false);
  };

  const openTwoFactorSetupModal = () => {
    setIsOpenTwoFactorSetupModal(true);
  };

  const closeTwoFactorSetupModal = () => {
    setIsOpenTwoFactorSetupModal(false);
  };

  const handleTwoFactorSetupSuccess = () => {
    setTwoFactorEnabled(true);
    setIsForceResetTwoFactor(false);
  };

  const validatePassword = (password: string): boolean => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/;
    return regex.test(password);
  };

  const isFormValid =
    resetPasswordData.current_password !== "" &&
    resetPasswordData.new_password !== "" &&
    resetPasswordData.confirm_new_password !== "" &&
    resetPasswordData.new_password === resetPasswordData.confirm_new_password;

  return (
    <>
      {isOpenTwoFactorConfirmModal && (
        <TwoFactorConfirmModal
          setSwalProps={setSwalProps}
          isOpen={isOpenTwoFactorConfirmModal}
          onClose={closeTwoFactorConfirmModal}
          isEnabling={shouldShowEnableButton}
          setTwoFactorEnabled={setTwoFactorEnabled}
          userRole={userRole}
          username={username}
          onOpenSetupModal={openTwoFactorSetupModal}
        />
      )}

      {isOpenTwoFactorSetupModal && (
        <TwoFactorSetupModal
          setSwalProps={setSwalProps}
          isOpen={isOpenTwoFactorSetupModal}
          onClose={closeTwoFactorSetupModal}
          onSuccess={handleTwoFactorSetupSuccess}
          userRole={userRole}
          username={username}
          userEmail={userEmail}
        />
      )}

      <div className="p-7">
        <div className="mb-5 text-sm text-navy-700 dark:text-white">
          <h3 className="text-base font-semibold mb-2">Change Password</h3>
          <p className="mb-6">
            For security reasons, you must enter your current password to
            proceed with changing your password.
          </p>

          {/* Form fields container */}
          <div className="w-full max-w-md">
            <InputField
              disabled={isProcessing}
              variant="auth"
              extra="mb-3 shadow-sm"
              label="Current Password"
              required={true}
              placeholder=""
              id="current_password"
              type="password"
              autoComplete="off"
              value={resetPasswordData.current_password}
              onChange={handleInputChange}
            />

            <div className="flex justify-center">
              <hr className="mb-5 mt-3 w-full" />
            </div>

            <InputField
              disabled={isProcessing}
              variant="auth"
              extra="mb-3 shadow-sm"
              label="New Password"
              required={true}
              placeholder=""
              id="new_password"
              type="password"
              autoComplete="off"
              value={resetPasswordData.new_password}
              onChange={handleInputChange}
            />

            <InputField
              disabled={isProcessing}
              variant="auth"
              extra="mb-3 shadow-sm"
              label="Confirm New Password"
              required={true}
              placeholder=""
              id="confirm_new_password"
              type="password"
              autoComplete="off"
              value={resetPasswordData.confirm_new_password}
              onChange={handleInputChange}
            />

            {/* Validation Messages */}
            {resetPasswordData.new_password !==
              resetPasswordData.confirm_new_password &&
              resetPasswordData.confirm_new_password !== "" && (
                <p className="my-2 text-sm text-red-500">
                  The new password and confirmation password must match.
                </p>
              )}

            {currentPasswordIncorrect && (
              <p className="my-2 text-sm text-red-500">
                Current password is incorrect, please try again.
              </p>
            )}

            {!validatePassword(resetPasswordData.new_password) &&
              resetPasswordData.new_password !== "" && (
                <p className="my-2 text-sm text-red-500">
                  The password must be at least 8 characters long and include at
                  least one uppercase letter, one lowercase letter, one number,
                  and one special character (@$!%*?&).
                </p>
              )}
          </div>

          {/* Button aligned right */}
          <div className="mt-4 flex justify-start">
            <Button
              disabled={!isFormValid || isProcessing}
              onClick={handleSubmit}
              isLoading={isProcessing}
              loadingText="Processing..."
              className="btn btn-primary"
            >
              Change Password
            </Button>
          </div>
        </div>

        {/* Two-Factor Authentication Section */}
        {userProfile?.isMFAEnabledInPool && (
          <div className="border-t border-gray-200 pt-7">
            <div className="mb-5 text-sm text-navy-700 dark:text-white">
              <div className="mb-4">
                <h3 className="text-base font-semibold mb-2">Two-Factor Authentication</h3>
                <p className="mb-2">
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>
              </div>

              <div className="flex justify-start">
                <Button
                  disabled={isProcessing}
                  onClick={handleTwoFactorToggle}
                  className="btn btn-primary"
                >
                  {shouldShowEnableButton ? "Enable Two-Factor" : "Disable Two-Factor"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SecurityTab;